import { Elastic, Sine, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Topics from "../Data/Topics";
// import { CaptionToken } from "../Data/CaptionToken";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";

export class Button extends HLF.Button implements HLF.Input.IFocusable, IOnboardingTarget {
    protected static readonly SCALE_NORMAL: number = 1.0;
    protected static readonly SCALE_OVER: number = 1.05;
    protected static readonly SCALE_DOWN: number = 0.95;

    protected _hoverVO: string | undefined;
    protected _clickedSFX: string | undefined;
    protected _autoPulse: boolean;

    protected _pulseAnim: TimelineMax | undefined;

    constructor(pCallback: () => void, pAsset: string, pSheet: string, pHoverVO?: string, pClickedSFX?: string) {
        super(pCallback, pAsset, pSheet);
        this._hoverVO = pHoverVO;
        this._clickedSFX = pClickedSFX;
        this._autoPulse = false;
    }

    public get autoPulse(): boolean {
        return this._autoPulse;
    }

    public set autoPulse(pValue: boolean) {
        this._autoPulse = pValue;
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        delete this._callback;
        if (this._pulseAnim !== undefined) { this._pulseAnim.kill(); }
        super.destroy(pOptions);
    }

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        this.stopPulse();
        this.playHoverVO();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this._callback();
            this.playClickedSFX();
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
        if (this._autoPulse) {
            this.playPulse();
        }
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width, this.height);
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    public playPulse(): void {
        if (this._pulseAnim !== undefined) { this._pulseAnim.kill(); }

        this._pulseAnim = new TimelineMax({
            repeat: -1,
        });

        this._pulseAnim.to(this._visuals.scale, 0.15, {
            x: 1.15,
            y: 1.15,
            ease: Sine.easeInOut,
        }, 3);
        this._pulseAnim.to(this._visuals.scale, 1.0, {
            x: 1,
            y: 1,
            ease: Elastic.easeOut,
        }, 3.15);
    }

    public stopPulse(): void {
        if (this._pulseAnim !== undefined) { this._pulseAnim.kill(); }
    }

    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOver(pEvent);
        TweenLite.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_OVER,
            y: Button.SCALE_OVER,
            ease: Sine.easeInOut,
        });
        this.playHoverVO();
        this.stopPulse();
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent);
        TweenLite.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_DOWN,
            y: Button.SCALE_DOWN,
            ease: Sine.easeInOut,
        });
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined) {
            TweenLite.to(this._visuals.scale, 0.15, {
                x: Button.SCALE_OVER,
                y: Button.SCALE_OVER,
                ease: Sine.easeInOut,
            });
            this.playClickedSFX();
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOut(pEvent);
        TweenLite.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_NORMAL,
            y: Button.SCALE_NORMAL,
            ease: Sine.easeInOut,
        });
        if (this._autoPulse) {
            this.playPulse();
        }
    }

    protected playHoverVO(): void {
        if (this._hoverVO !== undefined) {
            Application.instance.voiceOver.play(this._hoverVO, InteruptType.EQUAL, VoiceCategory.UI);
        }
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSFX, 1, false, HLF.AudioCategory.SFX));
        }
    }
}
